import { Plus, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Button, DataTable } from "../../../components/ui";
import { TableActions } from "../../../components/ui/TableActions";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds";
import AddTermInfoModal from "../modals/AddTermInfoModal";
import { settingsTermService } from "../services/settingsTermService";

export default function TermInfoTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTerm, setEditingTerm] = useState(null);
  const [viewingTerm, setViewingTerm] = useState(null);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const res = await settingsTermService.getTerms();
      if (res.success) {
        setTerms(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch terms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  const openCreate = () => {
    setEditingTerm(null);
    setViewingTerm(null);
    setModalOpen(true);
  };

  const openEdit = (term) => {
    setEditingTerm(term);
    setViewingTerm(null);
    setModalOpen(true);
  };

  const openView = (term) => {
    setEditingTerm(null);
    setViewingTerm(term);
    setModalOpen(true);
  };

  const toggleDisable = async (id) => {
    const termToToggle = terms.find(t => t._id === id);
    if (!termToToggle) return;

    try {
      const newStatus = termToToggle.status === 'Active' ? 'Inactive' : 'Active';
      const res = await settingsTermService.updateTerm(id, { status: newStatus });
      if (res.success) {
        toast.success(`Term marked as ${newStatus}`);
        setTerms(terms.map(t => t._id === id ? { ...t, status: newStatus } : t));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveTerm = async (termData) => {
    try {
      if (editingTerm) {
        const res = await settingsTermService.updateTerm(editingTerm._id, termData);
        if (res.success) {
          toast.success("Term updated successfully");
          setTerms(terms.map(t => t._id === editingTerm._id ? res.data : t));
        }
      } else {
        const res = await settingsTermService.createTerm(termData);
        if (res.success) {
          toast.success("Term created successfully");
          setTerms([res.data, ...terms]);
        }
      }
      setModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const filteredTerms = terms.filter(term => 
    (term.title || "").toLowerCase().includes(search.toLowerCase()) || 
    (term.termId || "").toLowerCase().includes(search.toLowerCase()) ||
    (term.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      header: "Term ID",
      render: (item) => <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">{(item.termId || "—").replace('TI-', 'T&C-')}</span>
    },
    {
      header: "Terms and Conditions",
      render: (item) => (
        <div 
          className="text-slate-800 text-[11px] leading-relaxed max-w-[350px] [&>p]:m-0 [&>p]:mb-1 last:[&>p]:mb-0 whitespace-pre-wrap" 
          dangerouslySetInnerHTML={{ __html: item.description || "—" }}
        />
      )
    },
    {
      header: "Payment Terms",
      render: (item) => (
        <div 
          className="text-slate-600 text-[11px] leading-relaxed max-w-[350px] [&>p]:m-0 [&>p]:mb-1 last:[&>p]:mb-0 whitespace-pre-wrap" 
          dangerouslySetInnerHTML={{ __html: item.paymentDescription || "—" }}
        />
      )
    },
    {
      header: "Status",
      render: (item) => {
        const isActive = item.status === "Active";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${isActive
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/30'
                : 'bg-slate-50 text-slate-500 border-slate-200/30'
              }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {item.status}
          </span>
        );
      }
    },
    {
      header: "Created Date & Time",
      className: "w-36 text-center",
      render: (item) => <span className="text-[11px] font-semibold text-slate-600 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "—"}</span>
    },
    {
      header: "Actions",
      className: "text-center w-32",
      render: (item) => {
        const isInactiveStatus = item.status === "Inactive";
        return (
          <TableActions
            onView={() => openView(item)}
            onEdit={() => openEdit(item)}
            onToggle={() => toggleDisable(item._id)}
            isActive={item.status === 'Active'}
            disabled={isInactiveStatus}
          />
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none mb-2">
            Terms And Conditions
          </h3>
          <p className="text-[13px] text-slate-500 font-medium">
            Manage standard terms and conditions for quotations and projects
          </p>
        </div>
        <Button
          onClick={openCreate}
          variant="orange"
          size="md"
          leftIcon={<Plus size={16} />}
        >
          Add Term
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terms..."
            className="w-full h-9 pl-9 pr-4 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
          <svg className="absolute left-3 top-2.5 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </div>
      </div>

      <div className="card overflow-hidden relative min-h-[200px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
          </div>
        ) : null}
        <DataTable
          columns={columns}
          data={filteredTerms}
          backgroundImage={TABLE_BACKGROUNDS.teams}
          isRowInactive={(row) => row.status === 'Inactive'}
          getRowClassName={(row) => row.status === 'Inactive' ? 'bg-slate-100/90 border-y border-slate-200/80' : 'hover:bg-slate-50/50'}
        />
      </div>

      {modalOpen && (
        <AddTermInfoModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveTerm}
          initialData={editingTerm || viewingTerm}
          isViewOnly={!!viewingTerm}
        />
      )}
    </div>
  );
}
